import { Component, computed, DestroyRef, inject, OnInit, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, NavigationEnd, Router, RouterLink } from '@angular/router';
import { TranslatePipe } from '@ngx-translate/core';
import { filter } from 'rxjs';

interface BreadcrumbItem {
  label: string;
  routerLink?: string;
}

/** Route-path prefix to parent breadcrumb translation key mapping */
const ROUTE_GROUP_LABELS: Record<string, string> = {
  transactions: 'breadcrumb.transactions',
  users: 'breadcrumb.accessManagement',
  settings: 'breadcrumb.settings',
  compliance: 'breadcrumb.compliance',
  establishments: 'breadcrumb.establishments',
  'payment-links': 'breadcrumb.paymentLinks',
  'pos-terminals': 'breadcrumb.posTerminals',
  reports: 'breadcrumb.reports',
  roles: 'breadcrumb.accessManagement',
  permissions: 'breadcrumb.accessManagement',
  monitor: 'breadcrumb.monitor',
};

/** Route-path prefix to clickable group link mapping */
const ROUTE_GROUP_LINKS: Record<string, string> = {
  transactions: '/transactions',
  establishments: '/establishments',
  'payment-links': '/payment-links',
  'pos-terminals': '/pos-terminals',
  settings: '/settings',
  reports: '/reports',
};

@Component({
  selector: 'app-breadcrumb',
  standalone: true,
  imports: [RouterLink, TranslatePipe],
  template: `
    <nav class="layout-breadcrumb">
      <ol>
        @for (item of breadcrumbs(); track item.label; let last = $last) {
          <li>
            @if (item.routerLink) {
              <a [routerLink]="item.routerLink">{{ item.label | translate }}</a>
            } @else {
              {{ item.label | translate }}
            }
          </li>
          @if (!last) {
            <li class="layout-breadcrumb-chevron">/</li>
          }
        }
      </ol>
    </nav>
  `,
})
export class AppBreadcrumbComponent implements OnInit {
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);

  private readonly _breadcrumbs = signal<BreadcrumbItem[]>([]);
  readonly breadcrumbs = computed(() => this._breadcrumbs());

  ngOnInit(): void {
    // Build initial breadcrumbs
    this.updateBreadcrumbs();

    // Update on route change
    this.router.events
      .pipe(
        filter((e) => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(() => this.updateBreadcrumbs());
  }

  private updateBreadcrumbs(): void {
    const items: BreadcrumbItem[] = [];

    // Find the deepest activated route with breadcrumb data
    let route: ActivatedRoute | null = this.activatedRoute;
    let breadcrumbLabel: string | undefined;
    let breadcrumbParent: { label: string; link?: string } | null | undefined;

    while (route) {
      const data = route.snapshot.data;
      if (data?.['breadcrumb']) {
        breadcrumbLabel = data['breadcrumb'] as string;
      }
      if (data?.['breadcrumbParent'] !== undefined) {
        breadcrumbParent = data['breadcrumbParent'] as { label: string; link?: string } | null;
      }
      route = route.firstChild;
    }

    if (!breadcrumbLabel) {
      this._breadcrumbs.set([]);
      return;
    }

    // Use explicit breadcrumbParent if provided (null = no parent)
    if (breadcrumbParent !== undefined) {
      if (breadcrumbParent !== null) {
        items.push({ label: breadcrumbParent.label, routerLink: breadcrumbParent.link });
      }
    } else {
      // Fall back to URL-based group detection
      const url = this.router.url.split('?')[0];
      const segments = url.split('/').filter(Boolean);

      if (segments.length > 0) {
        const firstSegment = segments[0];
        const groupLabel = ROUTE_GROUP_LABELS[firstSegment];

        if (groupLabel && groupLabel !== breadcrumbLabel) {
          const groupLink = ROUTE_GROUP_LINKS[firstSegment];
          items.push({ label: groupLabel, routerLink: groupLink });
        }
      }
    }

    // Add the current page label (no link — it's the active page)
    items.push({ label: breadcrumbLabel });

    this._breadcrumbs.set(items);
  }
}
